import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState, type UIEvent } from "react";

import { formatRelativeConversationTime, type ScoutThreadListItem } from "./useScoutThreadsList";

const LOAD_MORE_ROW_MIN_HEIGHT_PX = 72;
const LOAD_MORE_TRIGGER_BUFFER_PX = 8;
const SEARCH_SCROLLBAR_WIDTH_PX = 12;
const SEARCH_SCROLLBAR_OUTSET_PX = 24;
const LOAD_MORE_CONTENT_RIGHT_INSET_PX = SEARCH_SCROLLBAR_OUTSET_PX + 16;
const UNTITLED_CHAT_LABEL = "Untitled chat";
const LOAD_MORE_LABEL = "Load more";

export function ScoutThreadListSection({
  heading,
  items,
  loading,
  hasMore,
  loadingMore,
  emptyMessage,
  errorMessage,
  onLoadMore,
  onSelectItem,
  compact = false,
  pageSize = 5,
  scrollable = true,
}: {
  heading: string;
  items: ScoutThreadListItem[];
  loading?: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  emptyMessage: string;
  errorMessage?: string;
  onLoadMore: () => Promise<void> | void;
  onSelectItem: (item: ScoutThreadListItem) => void;
  compact?: boolean;
  /* Used to show correct number of loading skeletons */
  pageSize?: number;
  /**
   * When false, the list flows in a parent scroll surface (no nested
   * scrollbar). Load-more uses an intersection sentinel instead of onScroll.
   */
  scrollable?: boolean;
}) {
  const theme = useTheme();
  const scrollSurfaceColor = theme.palette.background.paper;
  const scrollThumbColor = theme.palette.scrollbarThumb;
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      setOpenTooltipId(null);

      if (loading || loadingMore || !hasMore) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom <= LOAD_MORE_TRIGGER_BUFFER_PX;

      if (isNearBottom) {
        void onLoadMore();
      }
    },
    [hasMore, loading, loadingMore, onLoadMore]
  );

  // When embedded in a parent scroller, watch a sentinel instead of onScroll.
  useEffect(() => {
    if (scrollable || loading || loadingMore || !hasMore) {
      return undefined;
    }

    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: `${LOAD_MORE_TRIGGER_BUFFER_PX}px`,
        threshold: 0,
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore, scrollable]);

  const isOpenTooltipItemRendered =
    openTooltipId == null || items.some((item) => item.id === openTooltipId);
  if (!isOpenTooltipItemRendered) {
    setOpenTooltipId(null);
  }

  const listBody = (
    <>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          ...(scrollable
            ? {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: scrollSurfaceColor,
              }
            : {}),
          py: 1.5,
          pl: compact ? 1.5 : 0,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          fontWeight: 500,
          color: "text.secondary",
        }}
      >
        {heading}
      </Typography>
      <Box sx={{ pb: 1 }}>
        {loading ? (
          <List disablePadding>
            {Array.from({ length: pageSize }, (_, i) => (
              <ListItemButton key={i}>
                <Skeleton
                  data-testid="thread-list-skeleton"
                  variant="text"
                  width="100%"
                  height={40}
                />
              </ListItemButton>
            ))}
          </List>
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : items.length === 0 ? (
          <Box
            sx={{
              minHeight: 120,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item) => {
              const relativeUpdatedAt = formatRelativeConversationTime(item.updatedAt);
              const hasPreviewExcerpt = !compact && Boolean(item.previewExcerpt);
              const titleLabel = item.title ?? UNTITLED_CHAT_LABEL;

              return (
                <ListItemButton
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  sx={{
                    width: "100%",
                    alignItems: "stretch",
                    borderBottom: (listTheme) => `1px solid ${listTheme.palette.divider}`,
                    px: compact ? 1.5 : 2,
                    py: compact ? 2 : 1.4,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1.1,
                      }}
                    >
                      {compact ? (
                        <Tooltip
                          disableInteractive
                          leaveDelay={0}
                          leaveTouchDelay={0}
                          onClose={() => {
                            setOpenTooltipId((currentTooltipId) =>
                              currentTooltipId === item.id ? null : currentTooltipId
                            );
                          }}
                          onOpen={() => setOpenTooltipId(item.id)}
                          open={openTooltipId === item.id}
                          placement="left"
                          title={
                            <Stack spacing={0.5}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {titleLabel}
                              </Typography>
                              {hasPreviewExcerpt ? (
                                <Typography variant="caption">{item.previewExcerpt}</Typography>
                              ) : null}
                              {relativeUpdatedAt ? (
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                  {relativeUpdatedAt}
                                </Typography>
                              ) : null}
                            </Stack>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              fontWeight: 500,
                              minWidth: 0,
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {titleLabel}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            fontWeight: 500,
                            flexShrink: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {titleLabel}
                        </Typography>
                      )}

                      {hasPreviewExcerpt && !compact && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            minWidth: 0,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            opacity: 0.72,
                          }}
                        >
                          {item.previewExcerpt}
                        </Typography>
                      )}
                    </Box>

                    {!compact && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          opacity: 0.58,
                        }}
                      >
                        {relativeUpdatedAt}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              );
            })}
            {(hasMore || loadingMore) && (
              <ListItemButton
                disabled={loadingMore}
                onClick={() => {
                  if (!loadingMore) {
                    void onLoadMore();
                  }
                }}
                sx={{
                  minHeight: `${LOAD_MORE_ROW_MIN_HEIGHT_PX}px`,
                  justifyContent: "center",
                  borderTop: (listTheme) => `1px solid ${listTheme.palette.divider}`,
                  pr: compact ? 1.5 : `${LOAD_MORE_CONTENT_RIGHT_INSET_PX}px`,
                }}
              >
                {loadingMore ? (
                  <Stack
                    width="100%"
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading more
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {LOAD_MORE_LABEL}
                  </Typography>
                )}
              </ListItemButton>
            )}
          </List>
        )}
        {!scrollable && hasMore ? (
          <Box ref={loadMoreSentinelRef} aria-hidden sx={{ height: 1 }} />
        ) : null}
      </Box>
    </>
  );

  if (!scrollable) {
    return <Box sx={{ width: "100%" }}>{listBody}</Box>;
  }

  return (
    <Box
      sx={{
        flex: 1,
        height: 0,
        width: compact ? "100%" : `calc(100% + ${SEARCH_SCROLLBAR_OUTSET_PX}px)`,
        maxWidth: "none",
        marginRight: compact ? 0 : `-${SEARCH_SCROLLBAR_OUTSET_PX}px`,
        minHeight: 0,
        backgroundColor: scrollSurfaceColor,
        overflow: "auto",
        overflowX: "hidden",
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollbarColor: `${scrollThumbColor} ${scrollSurfaceColor}`,
        "&::-webkit-scrollbar": {
          width: `${SEARCH_SCROLLBAR_WIDTH_PX}px`,
          backgroundColor: scrollSurfaceColor,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: scrollSurfaceColor,
          border: "none",
          boxShadow: "none",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: scrollThumbColor,
          border: "none",
          borderRadius: 999,
        },
        "&::-webkit-scrollbar-corner": {
          backgroundColor: scrollSurfaceColor,
        },
      }}
      data-testid="thread-list-scroll-container"
      onScroll={handleScroll}
    >
      {listBody}
    </Box>
  );
}
